"use client";

import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import Image from "next/image";

interface MediaItem {
  src: string;
  alt: string;
}

export default function PartnerLogos() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const mediaItems: MediaItem[] = [
    {
      src: "/images/isreal1.jpg",
      alt: "Indo-Israel Agritech Collaboration Emblem Pin",
    },
    {
      src: "/images/isreal2.jpg",
      alt: "Signed Strategic MoU Agreement Page showing official signatures",
    },
    {
      src: "/images/partner.png",
      alt: "Official Partnership Launch Group Photo",
    }
  ];

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "unset";
  };

  const nextImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
  };

  const prevImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
  };

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen]);

  return (
    <div className="w-full mt-6">
      {/* Beautiful 3-Card Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
        {mediaItems.map((item, index) => (
          <div
            key={index}
            onClick={() => openLightbox(index)}
            className="group relative cursor-pointer bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 aspect-[4/3] w-full"
          >
            <Image
              src={item.src}
              alt={item.alt}
              fill
              sizes="(max-width: 768px) 100vw, 33vw"
              className="object-cover transition-transform duration-500 group-hover:scale-105"
            />

            {/* Hover Zoom Overlay */}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[1px]">
              <div className="bg-white/95 p-3 rounded-full shadow-lg transform translate-y-3 group-hover:translate-y-0 transition-transform duration-300">
                <Maximize2 className="w-5 h-5 text-[#0f9648]" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Premium Lightbox Overlay Modal */}
      {lightboxOpen && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex flex-col justify-between p-4 md:p-6 transition-all duration-300"
        >
          {/* Header */}
          <div className="flex items-center justify-end text-white w-full max-w-6xl mx-auto py-2">
            {/* Close */}
            <button
              onClick={closeLightbox}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 active:scale-95 cursor-pointer"
              aria-label="Close fullscreen view"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Interactive Nav & Main Image */}
          <div className="relative flex-1 flex items-center justify-center max-w-6xl mx-auto w-full group/nav">
            {/* Prev */}
            <button
              onClick={prevImage}
              className="absolute left-2 md:left-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 active:scale-95 md:opacity-0 group-hover/nav:opacity-100 cursor-pointer"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Container for the Natural Image */}
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[75vh] max-w-full flex items-center justify-center p-2"
            >
              <Image
                src={mediaItems[currentIndex].src}
                alt={mediaItems[currentIndex].alt}
                width={800}
                height={600}
                className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl transition-all duration-300 select-none w-auto h-auto"
                priority
              />
            </div>

            {/* Next */}
            <button
              onClick={nextImage}
              className="absolute right-2 md:right-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 active:scale-95 md:opacity-0 group-hover/nav:opacity-100 cursor-pointer"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Footer Captions */}
          <div className="w-full max-w-3xl mx-auto text-center text-gray-300 pb-4 px-4 flex flex-col gap-1.5">
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">
              Image {currentIndex + 1} of {mediaItems.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
